import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@glu/theming';
import { Button, PRIMARY, SECONDARY } from '@glu/buttons-react';
import classnames from 'classnames';
import moment from 'moment';
import get from 'lodash/get';
import template from 'lodash/template';

import styles from './retrieveWireImportMap.styles';

// utils
import AppPropTypes from '../../../../proptypes/AppPropTypes';
import { DATE_FORMAT, WORKSPACES } from '../../../../constants';

import importMapColumns, { messageRow } from './retrieveImportMapColumns';

// components
import Alert from '../../../alert/Alert';
import ImportMappingTable from '../../../importMapping/ImportMappingTable';
import Spinner from '../../../spinner/Spinner';
import withLocale from '../../../withLocale/withLocale';
import PlaceResolver, { variants } from '../../../placeResolver/PlaceResolver';

// api
import { deleteWireImportMap, retrieveWireImportMap } from '../../../../api/wireImports/service';

import {
  getDelimiters,
  getCharges,
  getEndOfRecordOptions
} from '../../../../api/payments/service';
import { headerDeterminedFor } from '../utils';

function RetrieveWireImportMapComponent({
  classes,
  closeModal,
  history,
  locale,
  location,
  mapId
}) {
  const [apiData, set] = useState(null);
  const [error, setError] = useState(null);
  const [failure, setFailure] = useState(null);

  useEffect(() => {
    const { paymentType } = location.state || {};

    retrieveWireImportMap({ action: 'View', mapId, paymentType }).subscribe(({ data }) => {
      if (get(data, '0.code')) {
        setFailure(locale['wireImports.mapErrors.default']);
        return;
      }
      set(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (failure) {
    return failure;
  }

  if (!apiData) {
    return <Spinner />;
  }

  const tableData = get(apiData, 'wireImportMapDetail').filter((f) => f.isDisplay === 'Y');

  // sort by position
  const records = tableData.sort((a, b) => (a.order > b.order ? 1 : -1));

  const onDeleteMap = () => {
    deleteWireImportMap({ mapId: get(apiData, 'wireImportMapHeader.mtMapId') }).subscribe(
      ({ data, success }) => {
        // use response to populate mess
        if (!success) {
          const code = get(data, '0.code');
          const map = get(apiData, 'wireImportMapHeader.mapName');

          const fallback = locale['wireImports.mapErrors.default'];
          const messages = {
            WIRE_IMPORT_MAP_DOESNT_EXIST: template(
              locale['wireImports.mapErrors.WIRE_IMPORT_MAP_DOESNT_EXIST']
            )({ map }),
            CANNOT_DELETE_WIRE_MAP: template(
              locale['wireImports.mapErrors.CANNOT_DELETE_WIRE_MAP']
            )({ map }),
            CORBA_CALL_EXCEPTION: fallback
          };

          setError(messages[code] || fallback);
        } else {
          history.push({
            pathname: location?.state?.previous || WORKSPACES.PYMTCNTR,
            state: {
              messages: [
                {
                  id: 1,
                  title: locale['payments.manageImportMaps.delete.succeeded'],
                  data: { mapName: get(apiData, 'wireImportMapHeader.mapName') },
                  success: true
                }
              ]
            }
          });
        }
      }
    );
  };

  const {
    createdBy,
    delimiter,
    endofRecordMethod,
    lastUsedBy,
    lastUsedDate,
    mapName,
    mapType,
    possibleActions,
    skipHeaderRecord,
    stripQuotes,
    wireType
  } = get(apiData, 'wireImportMapHeader');

  const mappings = {
    lineEndings: {
      'ccr/lf': 'CR/LF',
      llf: 'LF'
    },
    yesno: {
      0: 'No',
      Y: 'Yes',
      N: 'No'
    }
  };
  const actions = {
    modify: possibleActions.includes('Modify'),
    delete: possibleActions.includes('Delete')
  };

  const fixed = mapType.toLowerCase() === 'fixed';

  return (
    <PlaceResolver
      services={[getDelimiters, getCharges, getEndOfRecordOptions]}
      variant={variants.table}
    >
      {({
        results: [
          { dropdown: delimiters },
          { dropdown: charges },
          { dropdown: endOfRecordOptions }
        ]
      }) => {
        const delimiterFound = delimiters.source.find((del) => del.id === delimiter);
        const endOFRecordFound = endOfRecordOptions.source.find((e) => e.id === endofRecordMethod);
        const delimiterWord = delimiterFound && delimiterFound.name;
        const endOfRecordWord = endOFRecordFound && endOFRecordFound.name;
        const endOfRecordRecordLength =
          endofRecordMethod.indexOf('R') === 0 && `Record Length ${endofRecordMethod.slice(1)}`;

        return (
          <div className={classes.wrapper}>
            <h1 className={classes.title}>
              {template(locale['wireImports.viewImportMap'])({
                determinedFor: locale[`wireImports.${headerDeterminedFor[wireType]}`]
              })}
            </h1>

            {error && (
              <Alert className={classes.alert} variant="warning" onHide={() => setError(null)}>
                <p className={classes.alertTitle}>import map cannot be deleted</p>
                {error}
              </Alert>
            )}

            <section className={classes.topSection}>
              <h2 className={classes.sectionTitle}>Map Details</h2>
              <div className={classes.details}>
                <div className={classnames(classes.label, 'col-md-6')}>
                  {locale['payments.manageImportMaps.name']}
                </div>
                <div className={classnames('col-md-6')}>{mapName || '-'}</div>
                <div className={classnames(classes.label, 'col-md-6')}>
                  {locale['payments.manageImportMaps.paymentType']}
                </div>
                <div className={classnames('col-md-6')}>{wireType || '-'}</div>
                <div className={classnames(classes.label, 'col-md-6')}>
                  {locale['payments.manageImportMaps.mapType']}
                </div>
                <div className={classnames('col-md-6')}>{mapType || '-'}</div>
                <div className={classnames(classes.label, 'col-md-6')}>
                  {locale['payments.manageImportMaps.createdBy']}
                </div>
                <div className={classnames('col-md-6')}>{createdBy || '-'}</div>
                <div className={classnames(classes.label, 'col-md-6')}>
                  {locale['payments.manageImportMaps.lastUsedDate']}
                </div>
                <div className={classnames('col-md-6')}>
                  {lastUsedDate ? moment(lastUsedDate).format(DATE_FORMAT) : '-'}
                </div>
                <div className={classnames(classes.label, 'col-md-6')}>
                  {locale['payments.manageImportMaps.lastUsedBy']}
                </div>
                <div className={classnames('col-md-6')}>{lastUsedBy || '-'}</div>
              </div>
            </section>
            <section className={classes.topSection}>
              <h2 className={classes.sectionTitle}>Map Parameters</h2>
              <div className={classes.details}>
                {!fixed && (
                  <>
                    <div className={classnames(classes.label, 'col-md-6')}>
                      {locale['payments.manageImportMaps.delimiter']}
                    </div>
                    <div className={classnames('col-md-6')}>{delimiterWord || '-'}</div>
                  </>
                )}
                {!fixed && (
                  <>
                    <div className={classnames(classes.label, 'col-md-6')}>
                      {locale['payments.manageImportMaps.stripQuotes']}
                    </div>
                    <div className={classnames('col-md-6')}>
                      {mappings.yesno[stripQuotes] || '-'}
                    </div>
                  </>
                )}
                <div className={classnames(classes.label, 'col-md-6')}>
                  {locale['payments.manageImportMaps.skipFirstRecord']}
                </div>
                <div className={classnames('col-md-6')}>
                  {mappings.yesno[skipHeaderRecord] || skipHeaderRecord}
                </div>
                <div className={classnames(classes.label, 'col-md-6')}>
                  {locale['payments.manageImportMaps.endOfRecordMethod']}
                </div>
                <div className={classnames('col-md-6')}>
                  {endOfRecordWord || endOfRecordRecordLength || '-'}
                </div>
              </div>
            </section>
            <ImportMappingTable
              columns={importMapColumns({ locale, data: { charges, fixed } })}
              data={records}
              name="wire_map_table"
              rowKey="fieldPosition"
              classes={classes}
              messageRow={messageRow(locale, fixed)}
            />
            <div className={classes.buttons}>
              {actions.delete && (
                <Button className="btn btn-primary" variant={PRIMARY} onClick={onDeleteMap}>
                  {locale['common.delete']}
                </Button>
              )}

              {actions.modify && (
                <Button
                  className="btn btn-primary"
                  variant={PRIMARY}
                  onClick={() =>
                    history.push({
                      pathname: `/payments/managePaymentMap/modify/wires/${mapId}`,
                      state: {
                        paymentType: wireType,
                        previous: location?.state?.previous || WORKSPACES.PYMTCNTR
                      }
                    })
                  }
                >
                  {locale['common.modify']}
                </Button>
              )}
              <Button
                className="btn btn-tertiary"
                variant={SECONDARY}
                onClick={() => closeModal()}
              >
                {locale['common.close']}
              </Button>
            </div>
          </div>
        );
      }}
    </PlaceResolver>
  );
}

RetrieveWireImportMapComponent.propTypes = {
  locale: AppPropTypes.locale.isRequired,
  mapId: PropTypes.number.isRequired,
  classes: AppPropTypes.jssClasses.isRequired,
  history: PropTypes.shape({}).isRequired,
  closeModal: PropTypes.func.isRequired,
  location: PropTypes.shape({}).isRequired
};

const Styled = withStyles(styles)(RetrieveWireImportMapComponent);
const Routed = withRouter(Styled);
const Localed = withLocale(Routed);
export default Localed;
